import Tracker from '@openreplay/tracker';
import trackerAssist from '@openreplay/tracker-assist';
import * as Sentry from '@sentry/vue';

let tracker: Tracker | null = null;

/**
 * Lazily creates the tracker singleton. Returns null when no project key is
 * configured (e.g. local dev without VITE_OPENREPLAY_PROJECT_KEY set), so
 * callers can no-op instead of the SDK throwing.
 */
function getTracker(): Tracker | null {
  const projectKey = import.meta.env.VITE_OPENREPLAY_PROJECT_KEY;
  if (!projectKey) return null;

  if (!tracker) {
    tracker = new Tracker({
      projectKey,
      // Defaults to OpenReplay's own cloud ingest endpoint when unset. Point
      // this at a self-hosted instance's ingest URL (e.g.
      // https://openreplay.example.com/ingest) once one is available.
      ingestPoint: import.meta.env.VITE_OPENREPLAY_INGEST_POINT || undefined,
      // Local dev only: OpenReplay refuses to record without HTTPS/a public
      // domain unless this is set. Never true in production.
      __DISABLE_SECURE_MODE: import.meta.env.DEV,
      // Powers the Data Management → People view in the OpenReplay dashboard.
      // Without this, tracker.analytics stays null and setProperties() below
      // is a no-op — session metadata (setMetadata, below) is a separate
      // system that only feeds the session player's metadata panel, not this.
      analytics: { active: true },
    });
    // Enables live-session viewing plus the Assist toolkit (call, remote
    // control/co-browsing, live chat) from the OpenReplay dashboard. Both
    // call and control still prompt the recorded user for consent before an
    // agent can join, so this is safe to enable unconditionally.
    tracker.use(trackerAssist({
      controlConfirm: {
        text: 'تیم فنی می‌خواد برای کمک به رفع مشکلتون، از راه دور به صفحه‌تون وصل بشه. اجازه می‌دید؟',
        confirmBtn: 'بله، اجازه می‌دم',
        declineBtn: 'نه، فعلاً نه',
        style: { direction: 'rtl', textAlign: 'right', fontFamily: 'inherit' },
      },
    }));
  }
  return tracker;
}

/**
 * Starts recording unconditionally on app boot, independent of login state,
 * so pre-login/anonymous activity (e.g. sign-in trouble) and post-logout
 * activity are captured too, not just authenticated sessions.
 *
 * Also tags the current Sentry scope with the OpenReplay session, once the
 * session actually starts, so a Sentry issue links straight to its replay
 * (see OpenReplay's Sentry integration, configured dashboard-side under
 * Preferences > Integrations, which reads these same tag names back out of
 * Sentry's API). Requires main.ts to call Sentry.init() before this, so the
 * tags land on an initialized client rather than being silently dropped.
 */
export function initSessionRecording(): void {
  const t = getTracker();
  if (!t) return;

  t.start().then(() => {
    const sessionId = t.getSessionID();
    const sessionURL = t.getSessionURL();
    if (sessionId) Sentry.setTag('openReplaySession.id', sessionId);
    if (sessionURL) Sentry.setTag('openReplaySessionURL', sessionURL);
  });
}

/**
 * Attaches identity to whatever session is already recording. Safe to call
 * repeatedly — on a fresh login, and again once the profile loads after a
 * page reload of an already-authenticated session.
 *
 * `roles` isn't available at login time (see stores/auth.ts), only once the
 * profile loads (stores/user.ts) — this call just omits role/department
 * metadata until the follow-up call from there supplies it. A user can hold
 * multiple roles, possibly spanning multiple departments (see Navbar.vue /
 * UserAccountMenu.vue), so both are joined rather than picking just one.
 *
 * Writes identity in two places, because OpenReplay treats them as unrelated
 * systems:
 * - setMetadata(): session-level metadata shown in the session player's
 *   panel. Only the keys pre-registered in the dashboard's Preferences >
 *   Metadata (max 10 per project) actually show up there — this silently
 *   drops anything else.
 * - analytics.people.setProperties(): what actually populates the Data
 *   Management → People page (e.g. .../data-management/user/<email>).
 *   Requires `analytics: { active: true }` on the Tracker (see getTracker());
 *   accepts arbitrary keys, no dashboard pre-registration needed.
 */
export function identifySessionUser(user: {
  id: number | string;
  name: string;
  email: string;
  avatar?: string;
  roles?: { title: string; department?: { title: string } }[];
}): void {
  const t = getTracker();
  if (!t) return;

  const role = user.roles?.length ? user.roles.map((r) => r.title).join(', ') : undefined;
  const department = user.roles?.length
    ? [...new Set(user.roles.map((r) => r.department?.title).filter(Boolean))].join(', ') || undefined
    : undefined;

  t.setUserID(user.email); // also triggers analytics.people.identify() internally

  t.setMetadata('name', user.name);
  t.setMetadata('userId', String(user.id));
  if (role) t.setMetadata('role', role);
  if (department) t.setMetadata('department', department);

  t.analytics?.people.setProperties({
    name: user.name,
    userId: String(user.id),
    email: user.email,
    ...(user.avatar ? { avatar: user.avatar } : {}),
    ...(role ? { role } : {}),
    ...(department ? { department } : {}),
  });
}
